/**
 * Real microphone level meter component.
 *
 * Renders a row of bars whose heights are driven by the `level` prop (0–1),
 * which should come from `useMicAudioLevel`. Unlike SpeakingWaveform this
 * component uses NO internal animation — every frame is driven purely by the
 * real audio amplitude passed from the parent.
 *
 * @module MicLevelMeter
 */

import { cn } from '@/lib/utils';

const BAR_COUNT = 20;
const MIN_HEIGHT = 8;  // % — floor so idle bars are still visible
const MAX_HEIGHT = 92; // % — ceiling so bars don't overflow the container

interface MicLevelMeterProps {
  /** Normalised amplitude 0–1 from useMicAudioLevel */
  readonly level: number;
  /** Whether the meter is live (true) or idle/inactive (false) */
  readonly isActive: boolean;
  readonly className?: string;
}

/**
 * Derives a per-bar height from the global level.
 * Centre bars are tallest; edge bars taper down for a rounded-mountain shape.
 */
function barHeight(index: number, level: number): number {
  // Gaussian envelope: centre of the bar row peaks highest
  const centre = (BAR_COUNT - 1) / 2;
  const spread = BAR_COUNT / 4;
  const envelope = Math.exp(-((index - centre) ** 2) / (2 * spread ** 2));
  const height = MIN_HEIGHT + (MAX_HEIGHT - MIN_HEIGHT) * level * envelope;
  return Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, height));
}

export function MicLevelMeter({ level, isActive, className }: MicLevelMeterProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-center gap-1 sm:gap-1.5 h-16 sm:h-20',
        className,
      )}
      role="meter"
      aria-label="Microphone level"
      aria-valuenow={Math.round(level * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      {Array.from({ length: BAR_COUNT }).map((_, i) => {
        const height = isActive ? barHeight(i, level) : MIN_HEIGHT;
        return (
          <div
            key={`mic-bar-${i}`}
            className={cn(
              'w-1.5 sm:w-2 rounded-full transition-all',
              // Faster transition while active so speech is responsive (<100 ms)
              isActive ? 'duration-75' : 'duration-300',
              isActive && level > 0.05
                ? 'bg-primary'
                : isActive
                  ? 'bg-primary/40'
                  : 'bg-muted-foreground/20',
              // Hide outer bars on very small screens
              (i === 0 || i === BAR_COUNT - 1) && 'hidden sm:block',
            )}
            style={{ height: `${height}%` }}
          />
        );
      })}
    </div>
  );
}
